/**
 * Histogramme ordinal comparé : abonnés contre jamais abonnés.
 *
 * ── Pourquoi la rampe séquentielle disparaît ici ────────────────────────────
 * L'histogramme global colore chaque seau d'un pas de la rampe : la couleur y
 * redouble l'ORDRE des tranches. Dès qu'il y a deux segments, la couleur doit
 * dire à quel SEGMENT appartient la barre — c'est l'information qu'on vient
 * chercher, et une barre ne peut pas encoder deux choses à la fois.
 *
 * L'ordre reste porté par l'axe des abscisses, qui suffit : les libellés sont
 * ordonnés et lus de gauche à droite. Les deux teintes sont celles des séries,
 * validées ensemble pour la surface sombre (ΔE CVD 11,2), et la légende les
 * double par du texte.
 *
 * ── Des PARTS et non des effectifs ──────────────────────────────────────────
 * Les deux segments n'ont presque jamais la même taille — il y a bien plus de
 * comptes jamais abonnés que d'abonnés. Comparer des effectifs bruts ferait une
 * rangée de barres minuscules contre une rangée de barres géantes, et on ne
 * lirait que la taille des groupes, jamais la forme des distributions.
 *
 * Chaque barre porte donc la part DANS SON SEGMENT. Les effectifs restent dans
 * la vue tableau, parce qu'une part de 50 % sur 8 comptes et sur 800 comptes ne
 * valent pas la même chose.
 */

import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import { SUBSCRIBER_SEGMENT_LABELS } from '@shared/types/stats';

import { formatInteger, formatPercent } from '../lib/format';
import { AXIS_STYLE, BAR_MAX_SIZE, COLORS } from '../lib/theme';
import type { DataColumn, SegmentedBucket } from '../types';
import { ChartCard } from './Card';
import { ChartLegend, ChartTooltip, type LegendEntry } from './ChartTooltip';

const legend: readonly LegendEntry[] = [
  { label: SUBSCRIBER_SEGMENT_LABELS.subscribers, color: COLORS.series1 },
  { label: SUBSCRIBER_SEGMENT_LABELS.nonSubscribers, color: COLORS.series2 },
];

interface Row {
  label: string;
  subscribers: number;
  nonSubscribers: number;
  subscriberShare: number;
  nonSubscriberShare: number;
}

const columns: ReadonlyArray<DataColumn<Row>> = [
  { key: 'label', header: 'Tranche', render: (row) => row.label },
  {
    key: 'subShare',
    header: 'Abonnés',
    numeric: true,
    render: (row) => formatPercent(row.subscriberShare),
  },
  {
    key: 'subUsers',
    header: 'Comptes',
    numeric: true,
    render: (row) => formatInteger(row.subscribers),
  },
  {
    key: 'nonShare',
    header: 'Jamais abonnés',
    numeric: true,
    render: (row) => formatPercent(row.nonSubscriberShare),
  },
  {
    key: 'nonUsers',
    header: 'Comptes',
    numeric: true,
    render: (row) => formatInteger(row.nonSubscribers),
  },
];

interface Props {
  data: readonly SegmentedBucket[];
  title: string;
  subtitle: string;
}

export function SegmentHistogram({ data, title, subtitle }: Props) {
  const totalSubscribers = data.reduce((sum, bucket) => sum + bucket.subscribers, 0);
  const totalNonSubscribers = data.reduce((sum, bucket) => sum + bucket.nonSubscribers, 0);

  const share = (count: number, total: number) => (total > 0 ? (count / total) * 100 : 0);

  const rows: Row[] = data.map((bucket) => ({
    ...bucket,
    subscriberShare: share(bucket.subscribers, totalSubscribers),
    nonSubscriberShare: share(bucket.nonSubscribers, totalNonSubscribers),
  }));

  return (
    <ChartCard title={title} subtitle={subtitle} rows={rows} columns={columns} chartHeight={300}>
      <ChartLegend entries={legend} />
      <ResponsiveContainer width="100%" height="86%">
        <BarChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={COLORS.grid} vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fill: COLORS.textSecondary, fontSize: 11 }}
            axisLine={{ stroke: COLORS.grid }}
            tickLine={false}
            interval={0}
          />
          <YAxis
            tickFormatter={(value: number) => `${value} %`}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={false}
            tickLine={false}
            width={48}
          />
          <Tooltip
            cursor={{ fill: 'rgba(255,255,255,0.05)' }}
            content={<ChartTooltip valueFormatter={formatPercent} />}
          />
          {/* Barres GROUPEES et non empilees : deux distributions independantes,
              dont la somme ne veut rien dire. Un empilement laisserait croire a
              un part-a-tout. */}
          <Bar
            isAnimationActive={false}
            dataKey="subscriberShare"
            name={SUBSCRIBER_SEGMENT_LABELS.subscribers}
            fill={COLORS.series1}
            radius={[4, 4, 0, 0]}
            maxBarSize={BAR_MAX_SIZE}
          />
          <Bar
            isAnimationActive={false}
            dataKey="nonSubscriberShare"
            name={SUBSCRIBER_SEGMENT_LABELS.nonSubscribers}
            fill={COLORS.series2}
            radius={[4, 4, 0, 0]}
            maxBarSize={BAR_MAX_SIZE}
          />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
